<template>
  <AddUpdateTargetUserForm
    :open="isUserFormVisible"
    :user="selectedUserForEdit"
    :mode="formMode"
    @close="closeTargetUserForm"
    @userAdded="refreshMembers"
    @alert="handleAlert"
    @userUpdated="refreshMembers"
  />

  <div class="bg-gray-50 px-4 py-6 sm:px-6">
    <div class="max-w-7xl mx-auto">
      <div
        class="mb-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h1 class="text-2xl font-semibold text-neutral-text mb-2">
            Members Management
          </h1>
          <div class="flex items-center gap-2">
            <span class="text-neutral-light">Total Members</span>
            <span
              class="bg-primary/10 text-primary-neutral text-sm font-medium px-2.5 py-0.5 rounded-full"
            >
              {{ totalMembers }}
            </span>
          </div>
        </div>
        <div
          class="flex w-full flex-row gap-2 sm:gap-3 sm:flex-row sm:flex-wrap md:w-auto md:justify-end"
        >
          <ActionButton
            action="add"
            variant="secondary"
            class="flex-1 py-2 text-xs sm:w-auto sm:py-2 sm:text-xs"
            @click="openAddMemberForm"
          >
            Add Member
          </ActionButton>
          <ActionButton
            action="filter"
            :active="showFilters"
            class="flex-1 py-2 text-xs sm:w-auto sm:py-2 sm:text-xs"
            @click="toggleFilters"
          >
            Filter
          </ActionButton>
        </div>
      </div>
      <MembersDataTable
        ref="membersTable"
        :show-filters="showFilters"
        @edit-member="handleEditMember"
        @member-deleted="refreshMembers"
        @member-updated="refreshMembers"
        @update-total="updateTotalMembers"
      />
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { getTargetUserById } from "../services/targetUsers";
import AddUpdateTargetUserForm from "../components/AddUpdateTargetUserForm.vue";
import MembersDataTable from "../components/dashboard/MembersDataTable.vue";
import ActionButton from "@/components/ui/ActionButton.vue";

const totalMembers = ref(0);
const showFilters = ref(false);
const isUserFormVisible = ref(false);
const selectedUserForEdit = ref(null);
const formMode = ref("add");
const membersTable = ref(null);

const updateTotalMembers = (count) => {
  totalMembers.value = count;
};

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const handleEditMember = async (member) => {
  try {
    const person = await getTargetUserById(member.id);
    selectedUserForEdit.value = person;
    isUserFormVisible.value = true;
    formMode.value = "edit";
  } catch {
    if (membersTable.value) {
      membersTable.value.showAlert({
        message: "Failed to get target user. Please try again later.",
        type: "dismiss",
      });
    }
  }
};

const handleAlert = (alert) => {
  if (membersTable.value) {
    membersTable.value.showAlert(alert);
  }
};

const refreshMembers = () => {
  if (membersTable.value) {
    membersTable.value.refreshMembers();
  }
};

const closeTargetUserForm = () => {
  isUserFormVisible.value = false;
  // Reset state after a short delay to allow modal animation to complete
  setTimeout(() => {
    selectedUserForEdit.value = null;
    formMode.value = "add";
  }, 200);
};

const openAddMemberForm = () => {
  selectedUserForEdit.value = null;
  formMode.value = "add";
  isUserFormVisible.value = true;
};
</script>

<style></style>
